import { ALTURA, LARGURA, UFS } from './ufsDoBrasil';

/**
 * O Brasil pintado por quantidade de leituras em cada UF (ADR-125), com o território declarado
 * por cima quando há um produto filtrado (ADR-128).
 *
 * <p>SVG embutido, sem biblioteca de mapas e sem imagem de mapa vinda de fora: um servidor de
 * mapas receberia o endereço de quem abre o painel a cada visita, e o que se mostra aqui é
 * contagem por estado — não precisa de rua.
 *
 * <p>A cor cresce pela raiz da proporção, e não linearmente: com São Paulo concentrando o volume,
 * a escala linear pintaria todo o resto do mesmo tom claro, e o mapa diria "só SP leu".
 *
 * <p>Com território: dentro dele, verde-azulado, e o contorno marca as UFs declaradas; fora dele,
 * âmbar — é a leitura onde o produto não deveria estar. Vermelho fica reservado a alerta e
 * marcação, como no resto do produto.
 *
 * <p>UF sem número é UF abaixo do limiar <b>ou</b> sem leitura — o mapa não distingue, de propósito:
 * dizer quais UFs têm entre 1 e 4 leituras seria revelar o que a supressão esconde.
 */
export function MapaDoBrasil({
  leituras,
  territorio,
}: {
  leituras: Record<string, number>;
  territorio?: string[] | null;
}) {
  const maior = Math.max(0, ...Object.values(leituras));
  const declarado = !!territorio && territorio.length > 0;

  function noTerritorio(uf: string): boolean {
    return !!territorio && (territorio.includes('BR') || territorio.includes(`BR-${uf}`));
  }

  function cor(uf: string, n: number | undefined): string {
    if (!n || maior === 0) {
      return 'var(--surface-2)';
    }
    const intensidade = Math.round(18 + 82 * Math.sqrt(n / maior));
    const base = declarado && !noTerritorio(uf) ? '#D08A12' : 'var(--teal)';
    return `color-mix(in srgb, ${base} ${intensidade}%, #ffffff)`;
  }

  return (
    <svg
      viewBox={`0 0 ${LARGURA} ${ALTURA}`}
      role="img"
      aria-label="Leituras por unidade federativa"
      style={{ width: '100%', maxWidth: 440, height: 'auto', display: 'block' }}
    >
      {UFS.map((u) => {
        const n = leituras[u.uf];
        const dentro = declarado && noTerritorio(u.uf);
        return (
          <path
            key={u.uf}
            d={u.d}
            fill={cor(u.uf, n)}
            stroke={dentro ? 'var(--navy)' : '#ffffff'}
            strokeWidth={dentro ? 3 : 1.6}
          >
            <title>
              {`${u.nome}: ${n ? `${n} leitura(s)` : 'abaixo do limiar ou sem leituras'}${
                declarado ? (dentro ? ' · no território' : ' · fora do território') : ''
              }`}
            </title>
          </path>
        );
      })}
    </svg>
  );
}
